'use client'

/**
 * Purpose: Accessible on/off switch (consent, marketing emails) following the WAI-ARIA switch
 * pattern — a native button with `role="switch"` so Enter/Space toggle it and screen readers
 * announce "on"/"off" rather than "pressed".
 * Inputs: `checked`, `onChange`, `ariaLabel`, optional `disabled` and `id` override.
 * Outputs: A `<button role="switch" aria-checked>` with a sliding knob.
 */

import './toggleSwitch.css'

interface ToggleSwitchProps {
  checked: boolean
  onChange: () => void
  ariaLabel: string
  disabled?: boolean
  /** Override when more than one ToggleSwitch renders on the same page. */
  id?: string
}

export function ToggleSwitch({ checked, onChange, ariaLabel, disabled, id = 'toggle-switch' }: ToggleSwitchProps) {
  // Native <button> gives us Enter/Space and focus for free; role="switch" + aria-checked
  // exposes the on/off state (aria-pressed would read as a toggle *button*, not a switch).
  return (
    <button
      id={id}
      type="button"
      role="switch"
      onClick={onChange}
      disabled={disabled}
      aria-checked={checked}
      aria-label={ariaLabel}
      className={`toggle-base ${checked ? 'toggle-on' : 'toggle-off'}`}
    >
      <span id={`${id}-knob`} aria-hidden="true" className={`toggle-knob ${checked ? 'toggle-knob-on' : 'toggle-knob-off'}`} />
    </button>
  )
}
